import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';

import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { TestAttributeValueDTO, TestAttributeValueFilterDTO, TestComponentFilterDTO, TestPackageDTO, TestPackageFilterDTO, TestScenarioDTO, TestScenarioFilterDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField } from '../../../shared/components';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { TestComponentDTO } from '../../../shared/interfaces/testingSphere/TestComponentDTO';
import { TestDataIntegrationDTO } from '../../../shared/interfaces/testingSphere/TestDataIntegrationDTO';
import { TestDataIntegrationFilterDTO } from '../../../shared/interfaces/testingSphere/TestDataIntegrationFilterDTO';
import { TestDataIntegrationMaintenance } from './TestDataIntegrationMaintenance';

interface ITestDataIntegrationProps {
    data: any;
    testScenarioId?: string;
}

export const TestDataIntegration: React.FC<ITestDataIntegrationProps> = ({ data, testScenarioId }) => {
    const [sourceTestPackageList, setSourceTestPackageList] = useState<TestPackageDTO[]>([]);
    const [sourceTestScenarioList, setSourceTestScenarioList] = useState<TestScenarioDTO[]>([]);
    const [sourceTestComponentList, setSourceTestComponentList] = useState<TestComponentDTO[]>([]);
    const [sourceTestAttributeValueList, setSourceTestAttributeValueList] = useState<TestAttributeValueDTO[]>([]);
    const [targetTestPackageList, setTargetTestPackageList] = useState<TestPackageDTO[]>([]);
    const [targetTestScenarioList, setTargetTestScenarioList] = useState<TestScenarioDTO[]>([]);
    const [targetTestComponentList, setTargetTestComponentList] = useState<TestComponentDTO[]>([]);
    const [targetTestAttributeValueList, setTargetTestAttributeValueList] = useState<TestAttributeValueDTO[]>([]);

    const [testDataIntegrationList, setTestDataIntegrationList] = useState<TestDataIntegrationDTO[]>([]);
    const [testDataIntegrationFilterDTO, setTestDataIntegrationFilterDTO] = useState<TestDataIntegrationFilterDTO>();

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');

    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    useEffect(() => {
        setTestDataIntegrationFilterDTO({
            ...testDataIntegrationFilterDTO,
            targetTestPackageId: '',
            targetTestScenarioId: testScenarioId ? data.id : '',
            targetTestComponentId: testScenarioId ? '' : data.id ?? '',
            targetTestAttributeValueId: '',
            sourceTestPackageId: '',
            sourceTestScenarioId: '',
            sourceTestComponentId: '',
            sourceTestAttributeValueId: ''
        } as TestDataIntegrationDTO);
    }, []);

    useEffect(() => {
        handleTargetTestPackageGetAllAsync();
        handleSourceTestPackageGetAllAsync();
        handleTestDataIntegrationtGetAllViewAsync();
    }, [testDataIntegrationFilterDTO]);

    useEffect(() => {
        handleTargetTestPackageGetAllAsync();
        handleSourceTestPackageGetAllAsync();
    }, []);

    useEffect(() => {
        if (actionExecuted)
            handleTestDataIntegrationtGetAllViewAsync();
    }, [actionExecuted]);

    //TARGET
    useEffect(() => {
        handleTargetTestScenarioGetAllAsync();
    }, [testDataIntegrationFilterDTO?.targetTestPackageId]);

    useEffect(() => {
        handleSourceTestScenarioGetAllAsync();
    }, [testDataIntegrationFilterDTO?.sourceTestPackageId]);

    useEffect(() => {
        handleTargetTestComponentGetAllAsync();
    }, [testDataIntegrationFilterDTO?.targetTestScenarioId]);

    useEffect(() => {
        handleSourceTestComponentGetAllAsync();
    }, [testDataIntegrationFilterDTO?.sourceTestScenarioId]);

    useEffect(() => {
        handleTargetTesAttributeValueGetAllViewAsync();
    }, [testDataIntegrationFilterDTO?.targetTestComponentId]);

    useEffect(() => {
        handleSourceTestAttributeValueGetAllViewAsync();
    }, [testDataIntegrationFilterDTO?.sourceTestAttributeValueId]);

    const handleTargetTestPackageGetAllAsync = useCallback(async () => {
        const testScenarioFilterDTO: TestPackageFilterDTO = {
            name: '',
            workspaceId: '',
        };
        GenericService.GetAllAsync1('testingsphere.testpackage', testScenarioFilterDTO, setLoading, setTargetTestPackageList, showMessage);
    }, []);

    const handleTargetTestScenarioGetAllAsync = useCallback(async () => {
        const testScenarioFilterDTO: TestScenarioFilterDTO = {
            name: '',
            testPackageId: testDataIntegrationFilterDTO?.targetTestPackageId,
        };
        GenericService.GetAllAsync1('testingsphere.testscenario', testScenarioFilterDTO, setLoading, setTargetTestScenarioList, showMessage);
    }, [testDataIntegrationFilterDTO?.targetTestPackageId]);

    const handleTargetTestComponentGetAllAsync = useCallback(async () => {
        const testComponentFilterDTO: TestComponentFilterDTO = {
            name: '',
            testScenarioId: testDataIntegrationFilterDTO?.targetTestScenarioId,
        };
        GenericService.GetAllAsync1('testingsphere.testcomponent', testComponentFilterDTO, setLoading, setTargetTestComponentList, showMessage);
    }, [testDataIntegrationFilterDTO]);

    const handleTargetTesAttributeValueGetAllViewAsync = useCallback(async () => {
        const testAttributeValueFilterDTO: TestAttributeValueFilterDTO = {
            name: '',
            testComponentId: ''
        };
        GenericService.GetAllViewAsync1('testingsphere.testattributevalue', testAttributeValueFilterDTO, setActionExecuted, setLoading, setTargetTestAttributeValueList, showMessage, false);
    }, [testDataIntegrationFilterDTO]);

    const handleSourceTestPackageGetAllAsync = useCallback(async () => {
        const testScenarioFilterDTO: TestPackageFilterDTO = {
            name: '',
            workspaceId: '',
        };
        GenericService.GetAllAsync1('testingsphere.testpackage', testScenarioFilterDTO, setLoading, setSourceTestPackageList, showMessage);
    }, []);

    const handleSourceTestScenarioGetAllAsync = useCallback(async () => {
        const testScenarioFilterDTO: TestScenarioFilterDTO = {
            name: '',
            testPackageId: testDataIntegrationFilterDTO?.sourceTestPackageId,
        };
        GenericService.GetAllAsync1('testingsphere.testscenario', testScenarioFilterDTO, setLoading, setSourceTestScenarioList, showMessage);
    }, []);

    const handleSourceTestComponentGetAllAsync = useCallback(async () => {
        const testComponentFilterDTO: TestComponentFilterDTO = {
            name: '',
            testScenarioId: testDataIntegrationFilterDTO?.targetTestScenarioId,
        };
        GenericService.GetAllAsync1('testingsphere.testcomponent', testComponentFilterDTO, setLoading, setSourceTestComponentList, showMessage);
    }, [testDataIntegrationFilterDTO]);

    const handleSourceTestAttributeValueGetAllViewAsync = useCallback(async () => {
        if(!testDataIntegrationFilterDTO?.sourceTestComponentId)
            return;

        const testAttributeValueFilterDTO: TestAttributeValueFilterDTO = {
            name: '',
            testComponentId: testDataIntegrationFilterDTO.sourceTestComponentId
        };
        GenericService.GetAllViewAsync1('testingsphere.testattributevalue', testAttributeValueFilterDTO, setActionExecuted, setLoading, setSourceTestAttributeValueList, showMessage, false);
    }, [testDataIntegrationFilterDTO?.sourceTestComponentId]);

    const handleTestDataIntegrationtGetAllViewAsync = useCallback(async () => {
        if (testDataIntegrationFilterDTO === undefined)
            return;

        GenericService.GetAllViewAsync1('testingsphere.testDataIntegration', testDataIntegrationFilterDTO, setActionExecuted, setLoading, setTestDataIntegrationList, showMessage, true);
    }, [testDataIntegrationFilterDTO]);

    const handleClearFilterClick = () => {
        setTestDataIntegrationFilterDTO({ ...testDataIntegrationFilterDTO, testComponentId: testScenarioId ?? data.id } as TestDataIntegrationFilterDTO);
    };

    function handleSearchClick() {
        handleTestDataIntegrationtGetAllViewAsync();
    }

    const handleNew = () => {
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setIsOpenMaintenance(true);

    };

    const handleDelete = (data: string) => {
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: 'New Test Data Integration', icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: 'View Details', icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: 'Delete', icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFilters: IChipFilter[] = [
        { label: 'Total Records', value: testDataIntegrationList.length },
        // { label: 'TestScenario', value: !data ? testScenarioList.find(x => x.id === testDataIntegrationFilterDTO?.targetTestScenarioId)?.name : undefined },
        { label: 'Source TestPackage', value: sourceTestPackageList.find(x => x.id === testDataIntegrationFilterDTO?.sourceTestPackageId)?.name },
        { label: 'Source TestScenario', value: sourceTestScenarioList.find(x => x.id === testDataIntegrationFilterDTO?.sourceTestScenarioId)?.name },
        { label: 'Source TestComponent', value: sourceTestComponentList.find(x => x.id === testDataIntegrationFilterDTO?.sourceTestComponentId)?.name },
        { label: 'Source TestAttributeValue', value: sourceTestAttributeValueList.find(x => x.id === testDataIntegrationFilterDTO?.sourceTestAttributeValueId)?.attributeId },
        { label: 'Target TestPackage', value: targetTestPackageList.find(x => x.id === testDataIntegrationFilterDTO?.targetTestPackageId)?.name },
        { label: 'Target TestScenario', value: targetTestScenarioList.find(x => x.id === testDataIntegrationFilterDTO?.targetTestScenarioId)?.name },
        { label: 'Target TestComponent', value: targetTestComponentList.find(x => x.id === testDataIntegrationFilterDTO?.targetTestComponentId)?.name },
        { label: 'Target TestAttributeValue', value: targetTestAttributeValueList.find(x => x.id === testDataIntegrationFilterDTO?.targetTestAttributeValueId)?.attributeId },

    ];

    const columns: ICardColumns[] = [
        { field: 'sourceTestPackageId', label: 'Source TestPackage', size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'sourceTestScenarioId', label: 'Source TestScenario', size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'sourceTestComponentId', label: 'Source TestComponent', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'sourceTestAttributeValueId', label: 'Source TestAttribute Value', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'targetTestPackageId', label: 'Target TestPackage', size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'targetTestScenarioId', label: 'Target TestScenario', size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'targetTestComponentId', label: 'Target TestComponent', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'targetTestAttributeValueId', label: 'Target TestAttribute Value', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='TEST DATA TRANSFER'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='sourceTestPackageId'
                                label='Source TestPackage'
                                value={testDataIntegrationFilterDTO?.sourceTestPackageId}
                                setValue={setTestDataIntegrationFilterDTO}
                                obj={testDataIntegrationFilterDTO}
                                valueList={sourceTestPackageList}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='sourceTestScenarioId'
                                label='Source TestScenario'
                                value={testDataIntegrationFilterDTO?.sourceTestScenarioId}
                                setValue={setTestDataIntegrationFilterDTO}
                                obj={testDataIntegrationFilterDTO}
                                valueList={sourceTestScenarioList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='sourceTestComponentId'
                                label='Source TestComponent'
                                value={testDataIntegrationFilterDTO?.sourceTestComponentId}
                                setValue={setTestDataIntegrationFilterDTO}
                                obj={testDataIntegrationFilterDTO}
                                valueList={sourceTestComponentList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='sourceTestAttributeValueId'
                                label='Source TestAttribute Value'
                                value={testDataIntegrationFilterDTO?.sourceTestAttributeValueId}
                                setValue={setTestDataIntegrationFilterDTO}
                                obj={testDataIntegrationFilterDTO}
                                valueList={sourceTestAttributeValueList}
                                hasNoneSelect={true}
                                indexKey={0}
                                indexValue={9}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='targetTestPackageId'
                                label='Target TestPackage'
                                value={testDataIntegrationFilterDTO?.targetTestPackageId}
                                setValue={setTestDataIntegrationFilterDTO}
                                obj={testDataIntegrationFilterDTO}
                                valueList={targetTestPackageList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='targetTestScenarioId'
                                label='Target TestScenario'
                                value={testDataIntegrationFilterDTO?.targetTestScenarioId}
                                setValue={setTestDataIntegrationFilterDTO}
                                obj={testDataIntegrationFilterDTO}
                                valueList={targetTestScenarioList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='targetTestComponentId'
                                label='Target TestComponent'
                                value={testDataIntegrationFilterDTO?.targetTestComponentId}
                                setValue={setTestDataIntegrationFilterDTO}
                                obj={testDataIntegrationFilterDTO}
                                valueList={targetTestComponentList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='targetTestAttributeValueId'
                                label='Target Test Attribute Value'
                                value={testDataIntegrationFilterDTO?.targetTestAttributeValueId}
                                setValue={setTestDataIntegrationFilterDTO}
                                obj={testDataIntegrationFilterDTO}
                                valueList={targetTestAttributeValueList}
                                hasNoneSelect={true}
                                indexKey={0}
                                indexValue={9}
                            />
                        </Grid>
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <CardList
                    columns={columns}
                    rows={testDataIntegrationList}
                    actions={cardActions}
                    hasChildren={false}
                    setRowExpanded={() => void undefined}
                    cardSize={12}
                />
                {newRecord && (
                    <TestDataIntegrationMaintenance
                        data={testScenarioId ?? data.id}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                        parentId={data.testScenarioId ?? testDataIntegrationFilterDTO?.targetTestScenarioId}

                    />
                )}
                {editRecord && (
                    <TestDataIntegrationMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'testingsphere.testdataintegration'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />)}
            </LayoutDashboard>
        </React.Fragment >
    );
};